import React, { useId, useMemo, useState } from "react";
import { AppWindow } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";

export type SliceData = {
  name: string;
  value: number;
  color: string;
  /** A known app logo, null for an unknown app, or omitted for non-app slices. */
  iconUri?: string | null;
  onClick?: () => void;
  description?: string;
  /** Render this slice with a diagonal hatch instead of a solid fill. */
  hatched?: boolean;
};

type SliceGeometry = {
  data: SliceData;
  startAngle: number;
  endAngle: number;
};

type DonutChartProps = {
  data: SliceData[];
  width?: number;
  height?: number;
  outerRadius?: number;
  innerRadius?: number;
  /** Hover driven from outside, as an index into `data`. Pass with `onHoverChange` when something
   *  beside the chart has to highlight in step with it; omit and the chart owns its hover. */
  hoveredIndex?: number | null;
  onHoverChange?: (index: number | null) => void;
  /** How a slice's raw `value` reads in the tooltip and its accessible name. */
  formatValue?: (value: number) => string;
};

const FULL_CIRCLE_EPSILON = 0.000001;

const DonutChart = ({
  data,
  width = 250,
  height = 250,
  outerRadius = 105,
  innerRadius = 48,
  hoveredIndex,
  onHoverChange,
  formatValue = String,
}: DonutChartProps) => {
  const total = useMemo(() => data.reduce((acc, item) => acc + item.value, 0), [data]);

  const slices = useMemo(() => {
    let startAngle = -Math.PI / 2;
    return data.map<SliceGeometry>(item => {
      const angle = (item.value / total) * 2 * Math.PI;
      const slice = { data: item, startAngle, endAngle: startAngle + angle };
      startAngle += angle;
      return slice;
    });
  }, [data, total]);

  // Controlled when the caller passes hoveredIndex, uncontrolled otherwise.
  const [ownHovered, setOwnHovered] = useState<number | null>(null);
  const isControlled = hoveredIndex !== undefined;
  const hovered = isControlled ? hoveredIndex : ownHovered;
  const setHovered = (index: number | null) => {
    if (!isControlled) setOwnHovered(index);
    onHoverChange?.(index);
  };

  const cx = width / 2;
  const cy = height / 2;
  const hoveredSlice = hovered === null ? null : (slices[hovered] ?? null);
  // SVG ids share one document-wide namespace, so on a page holding two donuts
  // (a run comparison) the second chart's `url(#…)` would resolve to the first
  // chart's pattern. The React id prefix keeps each chart on its own.
  // Colons are legal in an id but not in a CSS selector, and React's ids carry
  // them: dropped here so the pattern stays addressable by every reader of the
  // DOM, not only by `url(#…)`.
  const hatchPrefix = useId().replace(/:/g, "");
  const hatchId = (index: number) => `donut-hatch-${hatchPrefix}-${index}`;

  if (total === 0) {
    return null;
  }

  const describeArc = (startA: number, endA: number, r1: number, r2: number) => {
    const largeArc = endA - startA > Math.PI ? 1 : 0;
    const x1 = cx + r2 * Math.cos(startA);
    const y1 = cy + r2 * Math.sin(startA);
    const x2 = cx + r2 * Math.cos(endA);
    const y2 = cy + r2 * Math.sin(endA);
    const x3 = cx + r1 * Math.cos(endA);
    const y3 = cy + r1 * Math.sin(endA);
    const x4 = cx + r1 * Math.cos(startA);
    const y4 = cy + r1 * Math.sin(startA);

    return (
      `M ${x1} ${y1}` +
      ` A ${r2} ${r2} 0 ${largeArc} 1 ${x2} ${y2}` +
      ` L ${x3} ${y3}` +
      ` A ${r1} ${r1} 0 ${largeArc} 0 ${x4} ${y4}` +
      " Z"
    );
  };

  return (
    // Hover drives the tooltip, and it sits above the whole chart so it never hides the slices
    // or the cursor; it flips below only when the viewport has no room.
    <Tooltip.Provider>
      <Tooltip.Root open={!!hoveredSlice}>
        <Tooltip.Trigger asChild>
          <div style={{ width, height }}>
            <svg width={width} height={height}>
              <defs>
                {slices.map((slice, index) =>
                  slice.data.hatched ? (
                    <pattern
                      key={`hatch-${index}`}
                      id={hatchId(index)}
                      patternUnits="userSpaceOnUse"
                      width={6}
                      height={6}
                      patternTransform="rotate(45)"
                    >
                      <rect width={6} height={6} fill={slice.data.color} opacity={0.25} />
                      <line x1={0} y1={0} x2={0} y2={6} stroke={slice.data.color} strokeWidth={3} />
                    </pattern>
                  ) : null,
                )}
              </defs>
              {slices.map((slice, index) => {
                const isFullCircle =
                  slice.endAngle - slice.startAngle >= 2 * Math.PI - FULL_CIRCLE_EPSILON;
                const opacity = hovered === null || hovered === index ? 1 : 0.5;
                const paint = slice.data.hatched ? `url(#${hatchId(index)})` : slice.data.color;
                const isClickable = Boolean(slice.data.onClick);
                const interactiveProps = {
                  className: `${isClickable ? "cursor-pointer" : ""} transition-opacity duration-150`,
                  opacity,
                  onMouseEnter: () => setHovered(index),
                  onMouseLeave: () => setHovered(null),
                  onFocus: () => setHovered(index),
                  onBlur: () => setHovered(null),
                  onMouseDown: isClickable
                    ? (event: React.MouseEvent<SVGElement>) => {
                        event.preventDefault();
                      }
                    : undefined,
                  onClick: slice.data.onClick,
                  onKeyDown: isClickable
                    ? (event: React.KeyboardEvent<SVGElement>) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          slice.data.onClick?.();
                        }
                      }
                    : undefined,
                  tabIndex: isClickable ? 0 : undefined,
                  role: isClickable ? "button" : undefined,
                  "aria-label": isClickable
                    ? `${slice.data.name}: ${formatValue(slice.data.value)}`
                    : undefined,
                };

                if (isFullCircle) {
                  return (
                    <circle
                      key={`${slice.data.name}-${index}`}
                      cx={cx}
                      cy={cy}
                      r={(outerRadius + innerRadius) / 2}
                      fill="none"
                      stroke={paint}
                      strokeWidth={outerRadius - innerRadius}
                      {...interactiveProps}
                    />
                  );
                }

                return (
                  <path
                    key={`${slice.data.name}-${index}`}
                    d={describeArc(slice.startAngle, slice.endAngle, innerRadius, outerRadius)}
                    fill={paint}
                    stroke="var(--color-background)"
                    strokeWidth={2}
                    {...interactiveProps}
                  />
                );
              })}
            </svg>
          </div>
        </Tooltip.Trigger>
        {hoveredSlice && (
          <Tooltip.Portal>
            <Tooltip.Content
              side="top"
              sideOffset={8}
              collisionPadding={8}
              className="pointer-events-none z-[100] max-w-56 rounded-lg border border-outline bg-surface px-3 py-2.5 text-xs shadow-lg"
            >
              <div className="mb-1.5 flex items-center gap-2 font-semibold text-text">
                {hoveredSlice.data.iconUri ? (
                  <img
                    src={hoveredSlice.data.iconUri}
                    alt=""
                    className="h-5 w-5 shrink-0 object-contain"
                  />
                ) : hoveredSlice.data.iconUri === null ? (
                  <AppWindow className="h-5 w-5 shrink-0 text-text-dim" aria-hidden="true" />
                ) : null}
                <span>{hoveredSlice.data.name}</span>
              </div>
              <div className="text-text">
                {`${formatValue(hoveredSlice.data.value)} (${Math.round((hoveredSlice.data.value / total) * 100)}%)`}
              </div>
              {hoveredSlice.data.description && (
                <p className="mt-1 text-text-dim">{hoveredSlice.data.description}</p>
              )}
            </Tooltip.Content>
          </Tooltip.Portal>
        )}
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default DonutChart;
